import { PageContainer } from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { RouteName } from '@/navigation/types';

interface NotFoundStateBackTo {
    labelKey: TranslationKey;
    path: string;
}

interface NotFoundStateProps {
    titleKey: TranslationKey;
    messageKey: TranslationKey;
    /**
     * Where the way out leads. A page reached from a list sends the reader back to that list;
     * the rest keep the dashboard.
     */
    backTo?: NotFoundStateBackTo;
}

const BACK_TO_DASHBOARD: NotFoundStateBackTo = {
    labelKey: 'common.backToDashboard',
    path: RouteName.Evaluation360,
};

/**
 * What the module shows instead of an empty page when an address leads nowhere: a route it does
 * not serve, or a resource the server answers 404 for. The server answers the same 404 for
 * something that does not exist and for something the reader may not see, and so does this state:
 * nothing here confirms the existence of what someone is not allowed to look at.
 */
export const NotFoundState = ({
    titleKey,
    messageKey,
    backTo = BACK_TO_DASHBOARD,
}: NotFoundStateProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();

    return (
        <PageContainer>
            <EmptyState
                title={t(titleKey)}
                message={t(messageKey)}
                action={{
                    label: t(backTo.labelKey),
                    onClick: () => navigate({ path: backTo.path }),
                }}
            />
        </PageContainer>
    );
};
